// PostHog for Apsio's own console (plan step 2.4; D58, D59), on builds with VITE_POSTHOG_KEY.
//
// - The console asks the signed-in person one question, and keeps the answer on their profile
//   (PATCH /me analytics_consent), so it follows them across browsers. Before an answer, and
//   before sign-in, PostHog runs cookieless, as after a Reject (D58 addendum).
// - Granted: PostHog with its first-party storage, identified by the Apsio user id (the API's
//   business events use the same id), pageviews by path, error tracking, and session replay
//   with all text, inputs and text-bearing attributes masked, recorded only on the sign-up,
//   onboarding and billing pages (replayAllowed). Never on a page that shows an app's telemetry.
// - Denied: PostHog's cookieless mode (no cookie, no storage, no identity, no replay): pages and
//   errors are counted with a hash PostHog computes on its servers.
// - Every event leaves through scrubEvent: URLs keep their path (ids) and lose their query and
//   fragment (search text, filters), and an exception keeps its type, never its message.
// - PostHog's code is bundled (the no-external build and the recorder), so the CSP's script-src
//   stays 'self'; connect-src gains PostHog's hosts (security-headers.ts).
import type { CaptureResult, PostHog, PostHogConfig } from 'posthog-js/dist/module.no-external'
import { clearSignupHandoff, forgetSiteId, signupHandoff } from './signupHandoff'

export type Consent = 'granted' | 'denied'

const DEFAULT_HOST = 'https://us.i.posthog.com'

/** The build's PostHog project, or null: no prompt, no PostHog code at run time. */
export function analyticsConfig(
  env: { VITE_POSTHOG_KEY?: string; VITE_POSTHOG_HOST?: string } = import.meta.env,
): { key: string; host: string } | null {
  const key = env.VITE_POSTHOG_KEY?.trim()
  if (!key) return null
  const url = new URL(env.VITE_POSTHOG_HOST?.trim() || DEFAULT_HOST)
  if (url.protocol !== 'https:' && url.protocol !== 'http:')
    throw new Error('VITE_POSTHOG_HOST must be an http(s) URL')
  return { key, host: url.href.replace(/\/+$/, '') }
}

/**
 * The pages session replay may record (D58): sign-up and its first steps, onboarding, and the
 * plan and billing settings. Everything else, every view of an app's telemetry included, is never
 * recorded.
 */
export function replayAllowed(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/'
  if (['/signup', '/welcome'].includes(path)) return true
  if (/^\/o\/[^/]+\/new-project$/.test(path)) return true
  if (/^\/o\/[^/]+\/p\/[^/]+\/setup$/.test(path)) return true
  return /^\/o\/[^/]+\/p\/[^/]+\/settings\/plan$/.test(path)
}

/** A URL without its query and fragment: the path's ids stay, search text and filters go. */
function pathOnly(value: unknown): unknown {
  if (typeof value !== 'string') return value
  try {
    const url = new URL(value)
    return `${url.origin}${url.pathname}`
  } catch {
    return value.split(/[?#]/)[0]
  }
}

/**
 * Any property that holds a URL, by its name: $current_url and $referrer, and the ones PostHog
 * adds on its own ($session_entry_url, $session_entry_referrer, $initial_current_url in $set_once).
 */
const URL_KEY = /url|referrer|href/i

/** A property bag with every URL in it reduced to its path. */
function pathsOnly<B>(bag: B): B {
  if (!bag || typeof bag !== 'object' || Array.isArray(bag)) return bag
  const out: Record<string, unknown> = { ...(bag as Record<string, unknown>) }
  for (const [key, value] of Object.entries(out)) if (URL_KEY.test(key)) out[key] = pathOnly(value)
  return out as B
}

type Scrubbable = Pick<CaptureResult, 'event' | 'properties'> &
  Partial<Pick<CaptureResult, '$set' | '$set_once'>>

/**
 * What every event becomes before it leaves the browser: URLs reduced to their path (in the
 * properties and in what is set on the person), element text dropped, and each exception
 * reduced to its type (its message can quote data shown on the page, D59).
 */
export function scrubEvent<T extends Scrubbable>(event: T): T {
  const properties = pathsOnly({ ...event.properties })
  for (const key of ['$set', '$set_once'])
    if (key in properties) properties[key] = pathsOnly(properties[key])
  delete properties.$el_text
  if (event.event === '$exception' && Array.isArray(properties.$exception_list))
    properties.$exception_list = properties.$exception_list.map((e: Record<string, unknown>) => ({
      ...e,
      value: typeof e.type === 'string' ? e.type : 'Error',
    }))
  const out: T = { ...event, properties }
  if (event.$set) out.$set = pathsOnly(event.$set)
  if (event.$set_once) out.$set_once = pathsOnly(event.$set_once)
  return out
}

/** Attributes that carry page text; replay keeps the others (class, style, href) for layout. */
const TEXT_ATTRIBUTES = new Set(['alt', 'aria-label', 'content', 'placeholder', 'title', 'value'])

function posthogConfig(host: string): Partial<PostHogConfig> {
  return {
    api_host: host,
    defaults: '2026-08-30',
    // Cookieless until the person granted consent, and again if they withdraw it.
    cookieless_mode: 'on_reject',
    opt_out_capturing_by_default: true,
    persistence: 'localStorage+cookie',
    cross_subdomain_cookie: false,
    cookie_expiration: 365,
    person_profiles: 'identified_only',
    // Pages are captured by path on each route change (page()).
    capture_pageview: false,
    capture_pageleave: false,
    disable_external_dependency_loading: true,
    autocapture: false,
    rageclick: false,
    capture_heatmaps: false,
    capture_dead_clicks: false,
    capture_performance: false,
    // Error tracking (D59), scrubbed by before_send.
    capture_exceptions: true,
    disable_surveys: true,
    disable_product_tours: true,
    disable_conversations: true,
    disable_web_experiments: true,
    // Apsio's feature flags are evaluated by the API (D58).
    advanced_disable_feature_flags: true,
    enable_recording_console_log: false,
    mask_all_text: true,
    mask_all_element_attributes: true,
    mask_personal_data_properties: true,
    debug: false,
    // Started and stopped by route (replayAllowed).
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      maskAttributeFn: (name, value) =>
        TEXT_ATTRIBUTES.has(name) ? '*'.repeat(Math.min(value.length, 32)) : value,
      captureJsonLd: false,
      collectFonts: false,
      recordHeaders: false,
      recordBody: false,
      // The page's address in the replay (rrweb's Meta event) goes through here: its path only.
      maskCapturedNetworkRequestFn: (request) => ({
        ...request,
        name: String(pathOnly(request.name)),
      }),
    },
    before_send: (event) => (event ? scrubEvent(event) : event),
  }
}

/** Removes every cookie and storage entry PostHog may have written on this host. */
function forgetPostHog() {
  const posthogName = /^(__)?ph_/
  for (const pair of document.cookie.split(';')) {
    const name = pair.split('=')[0]?.trim() ?? ''
    // biome-ignore lint/suspicious/noDocumentCookie: the Cookie Store API is not in every browser the console supports
    if (posthogName.test(name)) document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`
  }
  for (const store of [localStorage, sessionStorage]) {
    try {
      for (const key of Object.keys(store)) if (posthogName.test(key)) store.removeItem(key)
    } catch {
      // Blocked storage holds nothing to remove.
    }
  }
}

/** The console's PostHog: one per page, driven by the profile's answer and the route. */
export class ConsoleAnalytics {
  private posthog: PostHog | undefined
  private applied: { consent: Consent | 'pending'; userId: string | undefined } | undefined
  private queue: Promise<unknown> = Promise.resolve()
  private location: { pathname: string; search: Record<string, unknown> } | undefined

  constructor(private readonly config: { key: string; host: string }) {}

  /** Applies the person's answer; answers apply one after another, however fast they come. */
  apply(consent: Consent | 'pending', userId?: string): Promise<unknown> {
    this.queue = this.queue.then(() => this.applyNow(consent, userId)).catch(() => undefined)
    return this.queue
  }

  private async applyNow(consent: Consent | 'pending', userId?: string) {
    if (this.applied?.consent === consent && this.applied.userId === userId) return
    if (consent === 'granted') await import('posthog-js/dist/posthog-recorder')
    if (!this.posthog) {
      if (consent !== 'granted') forgetPostHog()
      this.posthog = (await import('posthog-js/dist/module.no-external')).default
      this.posthog.init(this.config.key, posthogConfig(this.config.host))
    }
    const posthog = this.posthog
    if (consent === 'granted' && userId) {
      if (posthog.get_explicit_consent_status() !== 'granted')
        posthog.opt_in_capturing({ captureEventName: false })
      // The same id as the API's business events (D58); never an email or a name.
      posthog.identify(userId)
      // The site's anonymous visit that led here (APS-435), now part of this person.
      const siteId = signupHandoff()?.siteId
      if (siteId) {
        posthog.alias(siteId)
        forgetSiteId()
      }
    } else if (consent === 'pending' && this.applied?.consent !== 'granted') {
      // No answer yet (or nobody signed in): PostHog's own pending state, which counts as
      // rejected, cookieless and with nothing stored (D58 addendum).
    } else {
      posthog.stopSessionRecording()
      posthog.opt_out_capturing()
      forgetPostHog()
      // Declined here: the site's id is never tied to the account.
      if (consent === 'denied') forgetSiteId()
    }
    this.applied = { consent, userId }
    if (this.location) this.pageNow(this.location)
  }

  /** A route change: a pageview by path, and replay started or stopped for the new page. */
  page(pathname: string, search: Record<string, unknown>): void {
    this.location = { pathname, search }
    this.queue = this.queue.then(() => this.pageNow({ pathname, search })).catch(() => undefined)
  }

  private pageNow(location: { pathname: string; search: Record<string, unknown> }) {
    const posthog = this.posthog
    if (!posthog || !this.applied) return
    posthog.capture('$pageview')
    if (this.applied.consent === 'granted' && replayAllowed(location.pathname))
      posthog.startSessionRecording()
    else posthog.stopSessionRecording()
  }

  /** Sign-out: the next person on this browser starts anonymous. */
  reset(): void {
    clearSignupHandoff()
    this.posthog?.reset()
  }
}

let shared: ConsoleAnalytics | null | undefined
/** The page's analytics, or null on a build without a PostHog key. */
export function consoleAnalytics(): ConsoleAnalytics | null {
  if (shared === undefined) {
    // A build-time constant: built without a key, the PostHog chunks are never emitted.
    const config = import.meta.env.VITE_POSTHOG_KEY ? analyticsConfig() : null
    shared = config ? new ConsoleAnalytics(config) : null
  }
  return shared
}
